import { Injectable } from "@angular/core";
import * as companyDataActions from "@bca-company-data/store/company-data.actions";
import { RouterService } from "@bca-core/services/router.service";
import * as receiptActions from "@bca-receipt/store/receipt.actions";
import { ReceiptActionTypes } from "@bca-receipt/store/receipt.actions";
import { Route } from "@bca-shared/models/_enums";
import * as appActions from "@bca-store/app.actions";
import { IAction } from "@bca-store/models/action";
import { createAction, createActions } from "@bca-store/models/action-creator";
import { IRootState } from "@bca-store/store.reducers";
import { selectTotalProductCount } from "@bca-store/store.selectors";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { filter, map, mergeMap, tap, withLatestFrom } from "rxjs/operators";

@Injectable()
export class ReceiptEffects {
  selectUserFromHistoryUncheckedOnDeleteProduct = createEffect(() =>
    this._actions.pipe(
      ofType(ReceiptActionTypes.DeleteProduct),
      withLatestFrom(
        this._store$.pipe(
          select(({ companyData }) => companyData.selectedCompany)
        ),
        this._store$.pipe(select(({ compose }) => compose.selectedProduct)),
        this._store$.pipe(select(selectTotalProductCount))
      ),
      filter(
        ([, selectedCompany]) =>
          selectedCompany != null && selectedCompany.representative != null
      ),
      filter(
        ([action, selectedCompany, selectedProduct, totalProductCount]) => {
          const { user } = (action as receiptActions.DeleteProduct).payload;
          const { representative } = selectedCompany;

          if (selectedProduct && selectedProduct.user) {
            return (
              selectedProduct.user.id !== representative.id &&
              selectedProduct.user.id !== user.id
            );
          }
          return (
            totalProductCount <= 1 ||
            (user != null && user.id === representative.id)
          );
        }
      ),
      map(() => {
        return this._createAction(
          new companyDataActions.SelectUserFromHistoryChecked({
            checked: false,
          })
        );
      })
    )
  );

  navigateToComposeIfNoProducts = createEffect(
    () =>
      this._actions.pipe(
        ofType(ReceiptActionTypes.DeleteProduct),
        withLatestFrom(this._store$.pipe(select(selectTotalProductCount))),
        filter(([, totalProductCount]) => totalProductCount === 0),
        tap(() => this._routerService.navigateTo(Route.COMPOSE))
      ),
    { dispatch: false }
  );

  resetHighestRouteIfNoProduct = createEffect(() =>
    this._actions.pipe(
      ofType(ReceiptActionTypes.DeleteProduct),
      withLatestFrom(this._store$.pipe(select(selectTotalProductCount))),
      filter(([, totalProductCount]) => totalProductCount === 0),
      map(() => {
        return this._createAction(
          new appActions.SetHighestRoute({
            route: Route.COMPOSE,
          })
        );
      })
    )
  );

  deleteCompanyIfNoProducts = createEffect(() =>
    this._actions.pipe(
      ofType(ReceiptActionTypes.DeleteProduct),
      withLatestFrom(this._store$.pipe(select(selectTotalProductCount))),
      filter(([, totalProductCount]) => totalProductCount === 0),
      mergeMap(() =>
        this._createActions([
          new receiptActions.DeleteCompany(),
          new companyDataActions.SelectCompany(null),
        ])
      )
    )
  );

  private _createAction(action: IAction): IAction {
    return createAction(action, ReceiptEffects);
  }

  private _createActions(actions: IAction[]): IAction[] {
    return createActions(actions, ReceiptEffects);
  }

  constructor(
    private _actions: Actions,
    private _store$: Store<IRootState>,
    private _routerService: RouterService
  ) {}
}
